import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

interface DatePickerProps {
    label?: string;
    value: string; // 'yyyy-MM-dd'
    onChange: (value: string) => void;
    min?: string;
    max?: string;
    placeholder?: string;
}

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

function parseLocal(str: string): Date | null {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function toYMD(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export const DatePicker = ({ label, value, onChange, min, max, placeholder = 'Select date' }: DatePickerProps) => {
    const selected = parseLocal(value);
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState<Date>(() => selected ?? new Date());
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selected) setViewDate(selected);
    }, [value]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    // Grid: days in month padded to Mon-start grid
    const firstDay = new Date(year, month, 1);
    const startPad = (firstDay.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: (number | null)[] = [
        ...Array(startPad).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    // Pad end to full rows
    while (cells.length % 7 !== 0) cells.push(null);

    const selectDay = (day: number) => {
        const d = new Date(year, month, day);
        const ymd = toYMD(d);
        if (min && ymd < min) return;
        if (max && ymd > max) return;
        onChange(ymd);
        setOpen(false);
    };

    const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

    const isDisabled = (day: number) => {
        const ymd = toYMD(new Date(year, month, day));
        if (min && ymd < min) return true;
        if (max && ymd > max) return true;
        return false;
    };

    const isSelected = (day: number) => {
        if (!selected) return false;
        return selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === day;
    };

    const isToday = (day: number) => {
        const t = new Date();
        return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
    };

    const isWeekend = (colIdx: number) => colIdx === 5 || colIdx === 6; // Sa=5, Su=6

    const displayValue = selected
        ? selected.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

    return (
        <div className="w-full" ref={ref}>
            {label && (
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
            )}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm transition-all outline-none text-left
                        ${open
                            ? 'border-blue-500 ring-2 ring-blue-500/20'
                            : 'border-slate-200 hover:border-slate-300'
                        }
                        bg-white text-slate-900`}
                >
                    <span className={displayValue ? 'text-slate-900' : 'text-slate-400'}>
                        {displayValue || placeholder}
                    </span>
                    <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </button>

                {open && (
                    <div className="absolute z-50 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl p-5 w-88" style={{ width: '22rem' }}>
                        {/* Month/Year nav */}
                        <div className="flex items-center justify-between mb-3">
                            <button
                                type="button"
                                onClick={prevMonth}
                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-sm font-bold text-slate-800">
                                {MONTHS[month]} {year}
                            </span>
                            <button
                                type="button"
                                onClick={nextMonth}
                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Day-of-week headers */}
                        <div className="grid grid-cols-7 mb-1">
                            {DAYS.map((d, i) => (
                                <div
                                    key={d}
                                    className={`text-center text-xs font-semibold py-1
                                        ${i >= 5 ? 'text-slate-400' : 'text-slate-500'}`}
                                >
                                    {d}
                                </div>
                            ))}
                        </div>

                        {/* Day cells */}
                        <div className="grid grid-cols-7 gap-y-1">
                            {cells.map((day, idx) => {
                                const col = idx % 7;
                                if (day === null) {
                                    return <div key={idx} />;
                                }
                                const disabled = isDisabled(day);
                                const selected_ = isSelected(day);
                                const today = isToday(day);
                                const weekend = isWeekend(col);
                                return (
                                    <button
                                        key={idx}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => selectDay(day)}
                                        className={`h-10 w-full flex items-center justify-center rounded-lg text-sm font-medium transition-all
                                            ${selected_
                                                ? 'bg-blue-600 text-white shadow-sm'
                                                : today
                                                    ? 'bg-blue-50 text-blue-600 font-bold'
                                                    : disabled
                                                        ? 'text-slate-200 cursor-not-allowed'
                                                        : weekend
                                                            ? 'text-slate-400 hover:bg-slate-100'
                                                            : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Today shortcut */}
                        <div className="mt-3 pt-3 border-t border-slate-100">
                            <button
                                type="button"
                                onClick={() => { onChange(toYMD(new Date())); setOpen(false); }}
                                className="w-full text-center text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors py-1"
                            >
                                Today
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
